import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useScrollToTop from '@/hooks/useScrollToTop';

export default function AboutPage() {
  useScrollToTop();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value) {
          params.append(key, value.toString());
        }
      });
      const res = await fetch('https://readdy.ai/api/form/d9pmi9ub4qjhggaeq18g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { parsed = {}; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于91网址 - 精选网址导航平台',
            url: 'https://www.91wangzhi.com/about',
            description:
              '91网址是一站式网址导航平台，汇聚海量精选优质网址资源。了解我们的品牌故事和发展历程。',
            isPartOf: {
              '@type': 'WebSite',
              name: '91网址',
              url: 'https://www.91wangzhi.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '91网址',
            url: 'https://www.91wangzhi.com/',
            description:
              '一站式网址导航平台，汇聚海量精选优质网址资源。',
            email: 'contact@91wangzhi.com',
            address: {
              '@type': 'PostalAddress',
              addressLocality: '北京',
              addressCountry: 'CN',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@91wangzhi.com',
              contactType: 'customer service',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: '关于我们' },
                  ]}
                />
              </div>

              <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
                  关于91网址
                </h1>
                <div className="prose prose-sm md:prose-base max-w-none prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900">
                  <p className="text-lg text-foreground-600 leading-relaxed">
                    <strong>91网址</strong>成立于2024年，是一站式精选网址导航与资源汇聚平台。我们致力于为用户提供高效、纯净的上网入口，帮助每位网友快速找到所需网站，告别繁琐搜索，提升上网效率。
                  </p>
                  <p>
                    在信息爆炸的互联网时代，面对海量的网站和信息，用户往往需要花费大量时间去搜索和筛选真正有价值的网站。91网址导航的诞生，正是为了解决这一痛点——我们的编辑团队持续从互联网中精选优质、实用的网站资源，按照生活、娱乐、工具、学习等十大类别进行系统化整理，让用户能够一目了然地找到目标网站。
                  </p>
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">我们的理念</h3>
                  <p>
                    91网址坚持<strong>"精选而非海量"</strong>的核心理念。我们深知，真正对用户有价值的不是网站的数量，而是网站的质量。每一个收录的网站都经过严格的人工审核与筛选，确保推荐的都是高质量、实用性强的优质站点。我们相信，一个好的网址导航应该像一位贴心的向导，精准推荐而非信息轰炸。
                  </p>
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">我们的团队</h3>
                  <p>
                    91网址团队由一群热爱互联网、追求品质的年轻人组成，成员来自产品设计、技术开发和内容运营等多个领域。我们对互联网产品有着深刻的理解和独到的审美，坚持用专业的态度打磨每一个细节，为用户带来更好的上网体验。
                  </p>
                </div>
              </section>

              <section className="mb-16 bg-background-100 rounded-lg p-8 md:p-10">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                  平台信誉与数据
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">500+</div>
                    <p className="text-sm text-foreground-500">精选网址</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-2">10</div>
                    <p className="text-sm text-foreground-500">网址分类</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">50万+</div>
                    <p className="text-sm text-foreground-500">月访问用户</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-2">99.9%</div>
                    <p className="text-sm text-foreground-500">服务可用率</p>
                  </div>
                </div>
              </section>

              <section className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                  用户好评
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { name: '张先生', role: '互联网从业者', text: '91网址是我每天打开浏览器的第一站，分类清晰、推荐精准，极大提升了我的工作效率。强烈推荐给所有需要频繁访问各种网站的职场人！' },
                    { name: '李女士', role: '自由设计师', text: '作为一个设计师，91网址帮我收藏了很多优秀的设计资源和灵感网站。界面简洁好看，使用体验非常棒，已经成为我的日常必备工具。' },
                    { name: '王同学', role: '大学生', text: '学习资料、论文检索、在线课程...91网址的学习分类对我的帮助特别大。省去了不少搜索时间，可以把精力更多地放在学习本身。' },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                      <div className="flex items-center gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-secondary-500"></i>
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        "{review.text}"
                      </p>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                  联系我们
                </h2>
                <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg">
                      <i className="ri-mail-line text-xl text-primary-500"></i>
                    </span>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">电子邮箱</h4>
                    <p className="text-xs text-foreground-500">contact@91wangzhi.com</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                      <i className="ri-wechat-line text-xl text-accent-500"></i>
                    </span>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">微信公众号</h4>
                    <p className="text-xs text-foreground-500">91网址官方</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                    <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-lg">
                      <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                    </span>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">办公地址</h4>
                    <p className="text-xs text-foreground-500">中国 · 北京</p>
                  </div>
                </div>

                <div className="max-w-xl mx-auto bg-background-100 rounded-lg p-8 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6 text-center">
                    发送消息
                  </h3>
                  <form data-readdy-form="" onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form-honeypot" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full h-10 px-3 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full h-10 px-3 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="about-subject"
                        name="subject"
                        type="text"
                        className="w-full h-10 px-3 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 bg-red-50 px-4 py-2 rounded-md">{formError}</p>
                    )}
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 bg-accent-50 px-4 py-2 rounded-md">感谢您的留言！我们会尽快回复您。</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="whitespace-nowrap cursor-pointer w-full h-11 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-medium text-sm rounded-md transition-colors"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}